import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {skipped} from "#template/template-node/constants"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ObjectLikeNode} from "#template/nodes/types"
import {objectLike} from "#template/nodes/object/fundamental"
import {Translation} from "#template/nodes/rigid-relation/translation"
import {FixedRotation} from "#template/nodes/rigid-relation/rotation/fixed-rotation"
import {HingeRotation} from "#template/nodes/rigid-relation/rotation/hinge-rotation"
import {BallRotation} from "#template/nodes/rigid-relation/rotation/ball-rotation"
import type {Rotation} from "#template/nodes/rigid-relation/rotation/types"
import {R1Variable} from "#template/nodes/variable/r1-variable"
import {S1Variable} from "#template/nodes/variable/s1-variable"
import {S3Variable} from "#template/nodes/variable/s3-variable"

type Variable = R1Variable | S1Variable | S3Variable
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {SolverRelationData} from "#template/runtime-graph/nodes/solver/relation-data"
import type {SolverVariableData} from "#template/runtime-graph/nodes/solver/variable-data"
import {ThisStructID} from "#template/runtime-graph/types"
import {visitNone} from "#template/template-node/visit-helpers"

export type RigidRelationParameters = {
    targetA: ObjectLikeNode | null
    targetB: ObjectLikeNode | null
    translation: Translation
    rotation: Rotation
}

const rigidRelationDefinition: TemplateNodeDefinition<RigidRelationParameters> = {
    nodeClass: "RigidRelation",
    validation: {
        parameters: z.object({
            targetA: z.nullable(objectLike),
            targetB: z.nullable(objectLike),
            translation: graphNode(Translation),
            rotation: z.union([graphNode(FixedRotation), graphNode(HingeRotation), graphNode(BallRotation)]),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class RigidRelation extends TemplateNode<RigidRelationParameters> {
    public override get definition() {
        return rigidRelationDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<RigidRelationParameters> {
        if (this.parameters.targetA === null || this.parameters.targetB === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<RigidRelationParameters> {
        const {evaluator, currentTemplate} = context
        const {solverData} = currentTemplate
        const {translation, rotation, targetA, targetB} = this.parameters
        const {solverRelations} = solverData

        if (targetA === null || targetB === null) return skipped

        const scope = evaluator.getScope(this)

        const [targetAData, targetAInvalid] = scope.branch(evaluator.evaluate(targetA))
        const [targetBData, targetBInvalid] = scope.branch(evaluator.evaluate(targetB))

        const relation = scope.struct<SolverRelationData>("SolverRelationData", {
            id: ThisStructID,
            translation: this.setupTranslation(scope, context, translation),
            rotation: this.setupRotation(scope, context, rotation),
            objA: scope.get(targetAData, "solverObject"),
            objB: scope.get(targetBData, "solverObject"),
        })
        solverRelations.push(scope.phi(relation, targetAInvalid, targetBInvalid))

        return visitNone(this.parameters)
    }

    setupVariable(scope: GraphBuilderScope, context: OnCompileContext, variable: Variable): BuilderOutlet<SolverVariableData> {
        const {currentTemplate} = context
        const {solverData} = currentTemplate
        const {solverVariables} = solverData
        const getTopology = () => {
            if (variable instanceof R1Variable) return "boundedReal"
            else if (variable instanceof S1Variable) return "1-sphere"
            else if (variable instanceof S3Variable) return "3-sphere"
            else throw Error("Invalid variable type")
        }

        const solverVariable = scope.struct<SolverVariableData>("SolverVariableData", {
            id: ThisStructID,
            topology: getTopology(),
            default: variable.parameters.default,
            range: variable instanceof R1Variable ? variable.parameters.range : undefined,
        })

        solverVariables.push(solverVariable)

        return solverVariable
    }

    setupTranslation(scope: GraphBuilderScope, context: OnCompileContext, translation: Translation): BuilderOutlet<SolverRelationData["translation"]> {
        const {x, y, z} = translation.parameters

        return scope.struct<SolverRelationData["translation"]>("RelationTranslation", {
            tx: typeof x === "number" ? x : this.setupVariable(scope, context, x),
            ty: typeof y === "number" ? y : this.setupVariable(scope, context, y),
            tz: typeof z === "number" ? z : this.setupVariable(scope, context, z),
        })
    }

    setupRotation(scope: GraphBuilderScope, context: OnCompileContext, rotation: Rotation): BuilderOutlet<SolverRelationData["rotation"]> {
        if (rotation instanceof HingeRotation) {
            const {axis, rotation: rot} = rotation.parameters
            return scope.struct<SolverRelationData["rotation"]>("RelationRotation_hinge", {
                type: "hinge" as const,
                axis: axis,
                angleVariable: this.setupVariable(scope, context, rot),
            })
        } else if (rotation instanceof BallRotation) {
            const {rotation: rot} = rotation.parameters
            return scope.struct<SolverRelationData["rotation"]>("RelationRotation_ball", {
                type: "ball" as const,
                angleVariable: this.setupVariable(scope, context, rot),
            })
        } else if (rotation instanceof FixedRotation) {
            const {x, y, z} = rotation.parameters
            return scope.struct<SolverRelationData["rotation"]>("RelationRotation_fixed", {
                type: "fixed" as const,
                rx: x,
                ry: y,
                rz: z,
            })
        } else throw Error("Invalid rotation type")
    }
}
